import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { MyContextProvider } from "../../App";
import { API_URL } from '../../Constant';

const MONTHS = [
  { code: 'JAN', label: 'January' },
  { code: 'FEB', label: 'February' },
  { code: 'MAR', label: 'March' },
  { code: 'APR', label: 'April' },
  { code: 'MAY', label: 'May' },
  { code: 'JUN', label: 'June' },
  { code: 'JUL', label: 'July' },
  { code: 'AUG', label: 'August' },
  { code: 'SEP', label: 'September' },
  { code: 'OCT', label: 'October' },
  { code: 'NOV', label: 'November' },
  { code: 'DEC', label: 'December' },
];

const IIPCard = ({ handleCardClick, fullWidth = false, onDataChange }) => {
  const token = useContext(MyContextProvider).token;
  const [iipPendencyData, setIipPendencyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iipYear, setIipYear] = useState('');
  const [iipYearOptions, setIipYearOptions] = useState([]);
  const [iipMonth, setIipMonth] = useState(() => {
    const now = new Date();
    return ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][now.getMonth()];
  });

  // Generate year options dynamically
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let y = 2019; y <= currentYear; y++) {
      options.push(`${y}`);
    }
    setIipYearOptions(options.reverse());
    setIipYear(options[0] || '2019');
  }, []);

  useEffect(() => {
    if (!iipYear || !iipMonth) return;
    fetchIIPPendency();
  }, [iipYear, iipMonth]);

  const fetchIIPPendency = async () => {
    setLoading(true);
    try {
      const yearParam = iipYear.includes('-') ? iipYear.split('-')[0] : iipYear;
      const response = await axios.post(
        `${API_URL}top5-bottom5-month-year`,
        { survey_year: yearParam, month: iipMonth },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setIipPendencyData(response.data);
      // Notify parent component of data change
      if (onDataChange && response.data?.data) {
        onDataChange(response.data.data);
      }
    } catch (error) {
      setIipPendencyData(null);
      if (onDataChange) {
        onDataChange(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    transition: 'all 0.3s ease',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    borderLeft: '4px solid #ff8c00'
  };

  const gradientText = {
    background: 'linear-gradient(135deg, #ff8c00 0%, #ff4500 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  };

  // Indian currency formatting
  const formatCurrency = (num) => num !== undefined && num !== null && num !== '' ? `${Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '0.00';

  // Helper to render Top/Bottom 5 Industry table
  function IndustryTable({ title, data }) {
    return (
      <div className="mt-2">
        <b>{title}</b>
        <div className="asi-table-responsive">
          <table className="table table-bordered table-sm mt-1 asi-table-fixed-cols">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Industry Code</th>
                <th style={{ width: 120 }}>District</th>
                <th style={{ width: 260 }}>Industry Name</th>
                {/* <th style={{ width: 160 }}>Total Man Day Workers</th> */}
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.INDUSTRY_CODE}</td>
                  <td>{item.DISTRICT_NAME}</td>
                  <td>{item.INDUSTRIAL_UNIT_NAME}</td>
                  {/* <td>{formatCurrency(item.TOTAL_MANDAY_WORKERS)}</td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className={fullWidth ? "col-12 mb-4" : "col-xl-6 col-lg-6 mb-4"}>
      <div className="card h-100" style={cardStyle}>
        <div className="card-body">
          <div className="d-flex align-items-center mb-3 justify-content-between">
            <div className="d-flex align-items-center">
              <div className="fs-1 me-3" style={{ animation: 'pulse 2s infinite' }}>🏭</div>
              <div>
                <h5 className="card-title mb-0" style={gradientText}>Index of Industrial Production</h5>
                <p className="text-muted mb-0 small">IIP Statistics</p>
              </div>
            </div>
            {/* Year and Month Dropdowns */}
            <div className="d-flex align-items-center ms-3" style={{ minWidth: 320, gap: 8 }}>
              <label htmlFor="iip-year-select" className="form-label fw-bold mb-0 me-2">Year:</label>
              <select
                id="iip-year-select"
                className="form-select form-select-sm d-inline-block me-2"
                value={iipYear}
                onChange={e => setIipYear(e.target.value)}
                style={{ width: 80, height:35, display: 'inline-block' }}
              >
                {iipYearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <label htmlFor="iip-month-select" className="form-label fw-bold mb-0 me-2">Month:</label>
              <select
                id="iip-month-select"
                className="form-select form-select-sm d-inline-block"
                value={iipMonth}
                onChange={e => setIipMonth(e.target.value)}
                style={{ width: 120, height:35, display: 'inline-block' }}
              >
                {MONTHS.map(month => (
                  <option key={month.code} value={month.code}>{month.label}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center">Loading IIP data...</div>
          ) : (
            <>
              {/* Summary Stat Cards */}
              {iipPendencyData?.data?.summary && (
                <div className="row mb-3 d-flex justify-content-between">
                  <div className="col text-center">
                    <div className="fw-bold" style={{ color: '#ff8c00' }}>
                      {iipPendencyData.data.summary.total_divisions ?? "—"}
                    </div>
                    <small className="text-muted">Total Division</small>
                  </div>
                  <div className="col text-center">
                    <div className="fw-bold" style={{ color: '#28a745' }}>
                      {/* {iipPendencyData.data.summary.total_districts ?? "—"} */} 
                      75
                    </div>
                    <small className="text-muted">Total District</small>
                  </div>
                  {/* <div className="col text-center">
                    <div className="fw-bold" style={{ color: '#007bff' }}>
                      {iipPendencyData.data.summary.total_industrial_unit_codes ?? "—"}
                    </div>
                    <small className="text-muted">Total Industry Units</small>
                  </div> */}
                  {/* <div className="col text-center">
                    <div className="fw-bold" style={{ color: '#dc3545' }}>
                      {iipPendencyData.data.summary.total_address_industries ?? "—"}
                    </div>
                    <small className="text-muted">Total Industry Address</small>
                  </div> */}
                </div>
              )}

              {/* Top 5 and Bottom 5 Industry Details Tables */}
              {iipPendencyData?.data && (
                <>
                  <IndustryTable title="Top 5 Industry Details:" data={iipPendencyData.data.top5 || []} />
                  <IndustryTable title="Bottom 5 Industry Details:" data={iipPendencyData.data.bottom5 || []} />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IIPCard;
